import {
  KeyboardEvent,
  Fragment,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { Combobox } from '@headlessui/react';
import { Amplitude, Events } from '@iwoca/frontend-tracking-library';
import {
  fetchGetPostcodeLookup,
  GetPostcodeLookupResponse,
} from '@iwoca/lapi-client/edge';
import { Input, Label, Select, Spinner, Tooltip } from '@iwoca/orion';
import clsx from 'clsx';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { useLocale } from 'hooks/useLocale';
import { useFormContext } from 'react-hook-form';
import { buildAddressLabel } from 'utils/addressBuilder';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { PersonalDetailsType } from '../PersonalDetails/PersonalDetails';
import { TResidentialStatus, TSignupFormData } from '../SignupForm/SignupForm';
import { verifyUKCrownPostcode, verifyUKPostcode } from './addressHelpers';
import styles from './PostcodeInput.module.css';

type PostcodeAddressType = GetPostcodeLookupResponse['data'][number];

type ResidentialOptions = {
  label: string;
  value: TResidentialStatus;
};

const residentialOptions: ResidentialOptions[] = [
  {
    label: 'Tenant',
    value: 'tenant',
  },
  {
    label: 'Owner occupier (with mortgage)',
    value: 'owner_with_mortgage',
  },
  {
    label: 'Owner occupier (no mortgage)',
    value: 'owner_no_mortgage',
  },
  {
    label: 'Living rent free (e.g. with parents)',
    value: 'rent_free',
  },
];

type PostcodeInputType = {
  postcodeLabel?: string;
  addressOption?: 'primaryAddress' | 'secondaryAddress';
  personIndex: number;
  addressIndex: number;
  personRole: PersonalDetailsType['role'];
};

export const PostcodeInput = ({
  postcodeLabel,
  addressOption = 'primaryAddress',
  personRole,
  personIndex,
  addressIndex,
}: PostcodeInputType) => {
  const { locale } = useLocale();
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<TSignupFormData>();

  const ADDRESS_PROPERTY =
    personRole === 'applicant'
      ? (`applicant.residentialAddresses.${addressIndex}` as const)
      : (`guarantor.${personIndex}.residentialAddresses.${addressIndex}` as const);

  const ERROR_PROPERTY =
    personRole === 'applicant'
      ? errors.applicant?.residentialAddresses
      : errors?.[personRole]?.[personIndex]?.residentialAddresses;

  const [addresses, setAddresses] = useState<GetPostcodeLookupResponse['data']>(
    [],
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<PostcodeAddressType>();
  const [addressHasBeenSelected, setAddressHasBeenSelected] = useState(false);
  const [isManualAddress, setIsManualAddress] = useState(false);

  const address = getValues(`${ADDRESS_PROPERTY}`);
  const hasCompleteAddress =
    address &&
    Object.values(address).some(
      (formProperty) => formProperty !== null && formProperty !== '',
    );

  const showManualAddressButton =
    !isManualAddress && !hasCompleteAddress && !addressHasBeenSelected;

  useEffect(() => {
    const postcodeIsValidAndNotCrownDependency =
      verifyUKPostcode(searchQuery) && !verifyUKCrownPostcode(searchQuery);

    if (postcodeIsValidAndNotCrownDependency) {
      lookupPostcode(searchQuery);
    } else {
      setAddresses([]);
    }
  }, [searchQuery]);

  const lookupPostcode = async (postcode: string) => {
    try {
      setIsLoading(true);
      const addressResults = fetchGetPostcodeLookup({
        query: { postcode },
      });

      setAddresses((await addressResults).data);
    } catch {
      setAddresses([]);
    }
    setIsLoading(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isLoading) {
      e.preventDefault();
    }
  };

  const renderOptions = useCallback(() => {
    if (isLoading) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner dataTestId="postcode-spinner" />
        </div>
      );
    }

    if (addresses.length === 0) {
      return <p className={styles.noResults}>No results</p>;
    }

    return addresses.map((address, index) => (
      <Combobox.Option
        className={({ active }) =>
          clsx(styles.option, {
            [styles.active]: active,
          })
        }
        key={index}
        value={address}
      >
        {displayAddressValue(address)}
      </Combobox.Option>
    ));
  }, [addresses, isLoading]);

  const displayAddressValue = (address: PostcodeAddressType) => {
    const { house_name, house_number, street_line_1, town, postcode } = address;

    // TODO remove the below conditions once we fix undefined types for address details
    const addressLine = `${house_name && house_name} ${
      house_number && house_number
    } ${street_line_1 && street_line_1}`;

    return buildAddressLabel([addressLine, town, postcode]);
  };

  const onChange = (value: PostcodeAddressType) => {
    setSelectedAddress(value);

    if (!value) return;

    if (value.house_name) {
      setValue(`${ADDRESS_PROPERTY}.house_name`, value.house_name, {
        shouldValidate: true,
      });
    }

    if (value.house_number) {
      setValue(`${ADDRESS_PROPERTY}.house_number`, value.house_number, {
        shouldValidate: true,
      });
    }

    if (value.street_line_1) {
      setValue(`${ADDRESS_PROPERTY}.street_line_1`, value.street_line_1, {
        shouldValidate: true,
      });
    }

    if (value.street_line_2) {
      setValue(`${ADDRESS_PROPERTY}.street_line_2`, value.street_line_2, {
        shouldValidate: true,
      });
    }

    if (value.ptc_abs_code) {
      setValue(`${ADDRESS_PROPERTY}.equifax_token`, value.ptc_abs_code);
    }

    if (value.town) {
      setValue(`${ADDRESS_PROPERTY}.town`, value.town, {
        shouldValidate: true,
      });
    }

    if (value.postcode) {
      setValue(`${ADDRESS_PROPERTY}.postcode`, value.postcode, {
        shouldValidate: true,
      });
    }

    setAddressHasBeenSelected(true);
  };

  return (
    <>
      <Combobox nullable onChange={onChange}>
        <div className={clsx(styles.postcodeSearch)}>
          <div className={styles.postcodeLabelContainer}>
            <div className={styles.labelWithTooltip}>
              <Label>
                {postcodeLabel ? postcodeLabel : 'Residential postcode'}
              </Label>
              {addressOption === 'primaryAddress' && (
                <Tooltip content="If the address is not listed, please enter it manually." />
              )}
            </div>
            {showManualAddressButton && (
              <ButtonLink
                type="button"
                className={styles.manualAddressButton}
                data-testid="manual-address-btn"
                onClick={() => {
                  setIsManualAddress(true);
                  Amplitude.track(
                    Events.introducerPortal.introducerPortalEventCreator({
                      event: AMPLITUDE_EVENTS.manualAddressEntered,
                      locale,
                      payload: {
                        'Page name': document.title,
                        URL: window.location.pathname,
                        'CTA text': 'Enter manually',
                      },
                    }),
                  );
                }}
              >
                Enter manually
              </ButtonLink>
            )}
          </div>
          <Combobox.Input
            onChange={(event) => {
              setSearchQuery(event.target.value);
            }}
            as={Fragment}
            displayValue={() => searchQuery}
          >
            <Input
              iconRight="zoomOutline"
              placeholder="e.g. NW1 4NP"
              defaultValue={getValues(`${ADDRESS_PROPERTY}.postcode`)}
              data-testid="postcodeSearch"
              error={ERROR_PROPERTY?.[addressIndex]?.postcode?.message}
              onKeyDown={handleKeyDown}
            />
          </Combobox.Input>
          {searchQuery && (
            <Combobox.Options className={clsx(styles.options, 'fs-mask')}>
              {renderOptions()}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
      {(selectedAddress ||
        isManualAddress ||
        addressHasBeenSelected ||
        hasCompleteAddress) && (
        <>
          <div className={styles.addressRowContainer}>
            <Input
              {...register(`${ADDRESS_PROPERTY}.house_number`)}
              label="House number"
              defaultValue={getValues(`${ADDRESS_PROPERTY}.house_number`)}
              error={ERROR_PROPERTY?.[addressIndex]?.house_number?.message}
            />
            <Input
              {...register(`${ADDRESS_PROPERTY}.house_name`)}
              label="House name"
              defaultValue={getValues(`${ADDRESS_PROPERTY}.house_name`)}
              error={ERROR_PROPERTY?.[addressIndex]?.house_name?.message}
            />
          </div>
          <div className={styles.addressRowContainer}>
            <Input
              {...register(`${ADDRESS_PROPERTY}.street_line_1`)}
              label="Address Line 1"
              // Not sure if we need defaultValue here anymore
              defaultValue={getValues(`${ADDRESS_PROPERTY}.street_line_1`)}
              error={ERROR_PROPERTY?.[addressIndex]?.street_line_1?.message}
            />
            <Input
              {...register(`${ADDRESS_PROPERTY}.street_line_2`)}
              label="Address Line 2"
              defaultValue={getValues(`${ADDRESS_PROPERTY}.street_line_2`)}
              error={ERROR_PROPERTY?.[addressIndex]?.street_line_2?.message}
            />
          </div>
          <div className={styles.addressRowContainer}>
            <Input
              {...register(`${ADDRESS_PROPERTY}.town`)}
              label="Town/City"
              defaultValue={getValues(`${ADDRESS_PROPERTY}.town`)}
              error={ERROR_PROPERTY?.[addressIndex]?.town?.message}
            />
            <Input
              {...register(`${ADDRESS_PROPERTY}.postcode`)}
              label="Postcode"
              defaultValue={getValues(`${ADDRESS_PROPERTY}.postcode`)}
              error={ERROR_PROPERTY?.[addressIndex]?.postcode?.message}
            />
          </div>
        </>
      )}
      <div className={styles.residentialStatusWrapper}>
        <Select
          {...register(`${ADDRESS_PROPERTY}.residential_status`)}
          className={styles.residentialStatus}
          label="Residential status"
          error={ERROR_PROPERTY?.[addressIndex]?.residential_status?.message}
          defaultValue=""
        >
          <option value="" disabled>
            Choose an option
          </option>
          {residentialOptions.map((option) => {
            return (
              <option key={`${option.value}`} value={option.value}>
                {option.label}
              </option>
            );
          })}
        </Select>
      </div>
    </>
  );
};
